/* =========================================================================
 * IBC 2.0 — ADD-ONS AND THE PERSONALISED MESSAGE
 * -------------------------------------------------------------------------
 * Owns two things and nothing else:
 *
 *   1. the Triple Chocolate style add-on chooser, including the
 *      Milk / Dark / White chocolate choice and its min/max rules;
 *   2. the personalised message.
 *
 * Both contribute to add-to-cart through the published seam,
 * `window.IbcProduct.addSubmitHook()`, so assets/ibc-product.js never had
 * to learn about either of them. There is exactly ONE `POST /cart/add.js`
 * per add: the parent line and its children go in the same `items` array,
 * which is what keeps a bundle atomic.
 *
 * -------------------------------------------------------------------------
 * LINE-ITEM PROPERTY CONTRACT — deliverables/SHOPIFY_DATA_CONTRACTS.md §4
 * -------------------------------------------------------------------------
 * These spellings are load-bearing. The cart groups a parent with its child
 * ONLY when the parent's `Linked add-on key` string-equals the child's
 * `Linked cake key`; and the title-case / sentence-case split between
 * `Triple Chocolate Choice` (parent) and `Triple chocolate choice` (child)
 * is deliberate and read defensively in four places. Do not "tidy" either.
 *
 *   PARENT   Triple Chocolate            'Yes'
 *            Triple Chocolate Add-on     'Triple Chocolate for Whole Cake - Default Title'
 *            Triple Chocolate Choice     'Milk, Dark, White'
 *            Linked add-on key           'caprese-cake-whole-53528543494410-1755530412345'
 *            Personalised message        'HAPPY BIRTHDAY ANNA'      (NEW, this release)
 *
 *   CHILD    Added to cake               'Caprese Cake - Default Title'
 *            Cake product                'Caprese Cake Slice'
 *            Cake variant                'Default Title'
 *            Cake SKU                    ''                          (may be empty)
 *            Cake variant ID             '53528543494410'
 *            Triple chocolate size       'Default Title'
 *            Triple chocolate choice     'Milk, Dark, White'
 *            Linked cake key             same string as the parent's key
 *            Personalised message        'HAPPY BIRTHDAY ANNA'      (personalisation child only)
 *
 * PRESENTATION IS NOT LOGIC
 * -------------------------------------------------------------------------
 * On the full product page `assets/ibc-addon-flow.js` shows these controls
 * one at a time in a dialog instead of as an inline list. It MOVES the very
 * same elements in and out of that dialog and then calls the same
 * `addToCart()`, so this file remains the only place that decides what is
 * valid and the only place that names a line-item property. The three rules
 * the flow needs early — is this add-on valid, is this message valid, what
 * has been submitted — are published at the bottom of this file as thin
 * wrappers over the functions `contribution()` uses. Nothing is duplicated.
 *
 * `_bundle_removed` is listed in the contract but is written ONLY by the
 * cart, as a placeholder when rebuilding a parent's properties would leave
 * `{}` (which `POST /cart/change.js` rejects). The product page never
 * writes it. The constant is declared below so that whoever rebuilds the
 * cart copies the spelling from here rather than retyping it.
 * ====================================================================== */
(function () {
  'use strict';

  /* THE UNDERSCORE IS NOT DECORATION.
   *
   * Shopify treats a line-item property whose key starts with `_` as
   * PRIVATE: it is kept on the cart, kept on the order, and shown in the
   * admin, but it is not printed at checkout, in the order confirmation
   * email, or on the order status page. Everything here that exists only so
   * that code can find something later therefore carries one.
   *
   * Before this, checkout printed the lot under every add-on line —
   * `Cake variant ID: 53092873371914`, `Linked cake key:
   * caprese-cake-whole-53092873371914-1789212145348` — which is machinery
   * quoted at a customer about to pay.
   *
   * TWO ARE DELIBERATELY PUBLIC, and must stay that way:
   *
   *   `Personalised message`     what the customer typed and paid £10 for.
   *                              Checkout is their last chance to see the
   *                              spelling before it is piped onto a cake.
   *   `Triple chocolate choice`  the flavours they picked. A real choice,
   *                              and the only place checkout shows it.
   *
   * The theme's own cart needs no change for any of this:
   * snippets/ibc-cart-line.liquid already skips every property whose key
   * starts with `_` before it prints the list.
   *
   * THE OLD SPELLINGS ARE STILL READ. A cart created before this change
   * carries the unprefixed names, and Shopify carts live for weeks. Every
   * READER — assets/ibc-cart.js, snippets/ibc-cart-line(s).liquid — accepts
   * either spelling. This map is the WRITER, so it names only the new ones.
   * Orders already placed keep the old names for ever; anything reading
   * order history has to accept both, permanently. */
  var PROPS = {
    /* parent */
    tripleChocolate: '_Triple Chocolate',
    tripleChocolateAddOn: '_Triple Chocolate Add-on',
    tripleChocolateChoice: '_Triple Chocolate Choice',
    linkedAddOnKey: '_Linked add-on key',
    personalisedMessage: 'Personalised message',
    /* child */
    addedToCake: '_Added to cake',
    cakeProduct: '_Cake product',
    cakeVariant: '_Cake variant',
    cakeSku: '_Cake SKU',
    cakeVariantId: '_Cake variant ID',
    tripleChocolateSize: '_Triple chocolate size',
    tripleChocolateChoiceChild: 'Triple chocolate choice',
    linkedCakeKey: '_Linked cake key',
    /* cart-only recovery marker — never written here */
    bundleRemoved: '_bundle_removed'
  };

  var MSG = {
    noFlavour: 'Select at least one chocolate type.',
    oneFlavour: 'Choose one chocolate type.',
    maxFlavours: function (n) {
      return 'Choose up to ' + n + ' chocolate types.';
    },
    unsubmitted: 'Please submit your personalised message, or untick it.',
    tooLong: function (n) {
      return 'Please keep the message to ' + n + ' characters.';
    }
  };

  function toArray(list) {
    return Array.prototype.slice.call(list || []);
  }

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  /* The live theme's generator, unchanged:
   *   `${productHandle}-${variantId}-${Date.now()}`
   * One key per add-to-cart, shared by the parent and every child. */
  function bundleKey(handle, variantId) {
    return String(handle || 'product') + '-' + String(variantId) + '-' + Date.now();
  }

  /* ==================================================================== */
  /* ADD-ON PANELS                                                        */
  /* ==================================================================== */

  function addOnsIn(scope) {
    return toArray(scope.querySelectorAll('[data-ibc-addon]'));
  }

  function flavourInputs(addon) {
    return toArray(addon.querySelectorAll('input[data-ibc-addon-flavour]'));
  }

  function chosenFlavours(addon) {
    return flavourInputs(addon)
      .filter(function (input) {
        return input.checked;
      })
      .map(function (input) {
        return input.value;
      });
  }

  function maxFor(addon) {
    var raw = parseInt(addon.getAttribute('data-max'), 10);
    return isNaN(raw) || raw <= 0 ? null : raw;
  }

  function setAddOnError(addon, message) {
    var region = addon.querySelector('[data-ibc-addon-error]');
    if (!region) return;
    region.textContent = message || '';
    region.hidden = !message;
  }

  /* Show or hide the chocolate panel with the checkbox, and keep the
   * fieldset out of the tab order while it is closed. */
  function syncAddOn(addon) {
    var toggle = addon.querySelector('input[data-ibc-addon-toggle]');
    var choices = addon.querySelector('[data-ibc-addon-choices]');
    var on = !!(toggle && toggle.checked);
    addon.classList.toggle('is-on', on);
    if (choices) choices.hidden = !on;
    if (!on) setAddOnError(addon, '');
    else validateAddOn(addon, false);
  }

  /* Returns '' when valid, otherwise the message. `announce` writes it into
   * the inline error region as well. */
  function validateAddOn(addon, announce) {
    var toggle = addon.querySelector('input[data-ibc-addon-toggle]');
    if (!toggle || !toggle.checked) return '';
    if (addon.getAttribute('data-has-choice') !== 'true') return '';

    var picked = chosenFlavours(addon);
    var max = maxFor(addon);
    var message = '';

    if (!picked.length) {
      message = MSG.noFlavour;
    } else if (max && picked.length > max) {
      message = max === 1 ? MSG.oneFlavour : MSG.maxFlavours(max);
    }
    if (announce !== false) setAddOnError(addon, message);
    return message;
  }

  /* The live rule, preserved: a maximum of 1 behaves like a radio group
   * (the newest choice wins), anything else reverts the click that went
   * over the limit and explains why. */
  function onFlavourChange(addon, input) {
    var max = maxFor(addon);
    var picked = chosenFlavours(addon);

    if (max && picked.length > max) {
      if (max === 1) {
        flavourInputs(addon).forEach(function (other) {
          if (other !== input) other.checked = false;
        });
        setAddOnError(addon, '');
        return;
      }
      input.checked = false;
      setAddOnError(addon, MSG.maxFlavours(max));
      return;
    }
    validateAddOn(addon, true);
  }

  /* ==================================================================== */
  /* PERSONALISED MESSAGE                                                 */
  /* ==================================================================== */

  function personalisationIn(scope) {
    return scope.querySelector('[data-ibc-personalisation]');
  }

  function persState(element) {
    if (!element._ibcPers) element._ibcPers = { saved: '' };
    return element._ibcPers;
  }

  function persMaxLength(element) {
    var raw = parseInt(element.getAttribute('data-max-length'), 10);
    return isNaN(raw) || raw <= 0 ? 20 : raw;
  }

  function setPersError(element, message) {
    var region = element.querySelector('[data-ibc-pers-error]');
    if (!region) return;
    region.textContent = message || '';
    region.hidden = !message;
  }

  /* The personalised message's rules, in ONE place. `contribution()` below
   * calls it on submit and `assets/ibc-addon-flow.js` calls it (through
   * `window.IbcAddOns`) when the customer finishes that step, so the popup
   * and the add-to-cart cannot disagree about what a valid message is.
   * Returns '' when there is nothing wrong. */
  function persValidate(element) {
    var toggle = element.querySelector('[data-ibc-pers-toggle]');
    if (!toggle || !toggle.checked) return '';
    var state = persState(element);
    if (!state.saved) return MSG.unsubmitted;
    if (state.saved.length > persMaxLength(element)) return MSG.tooLong(persMaxLength(element));
    return '';
  }

  /* Put an element in the middle of what the customer can actually see.

     `scrollIntoView({ block: 'center' })` centres against the LAYOUT
     viewport, which on a phone still counts the area the on-screen keyboard
     is covering — so the field ends up centred behind the keyboard. This
     measures against `visualViewport` instead, which is the part that is
     genuinely visible, and falls back to the layout viewport where that API
     is missing.

     It runs twice on purpose. The keyboard is not up yet on the first pass,
     so the first scroll is smooth and roughly right; the second fires once
     the keyboard has resized the visual viewport and corrects the position,
     instantly, so the correction does not read as a second animation. If the
     viewport did not change — a desktop, or a device with a hardware
     keyboard — the second pass is skipped and nothing moves twice.

     No zoom is involved either way: that is the font-size rule in
     assets/ibc-addons.css, not this. */
  function centreInView(element) {
    if (!element || !element.getBoundingClientRect) return;

    var reduced =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var view = window.visualViewport || null;
    var startHeight = view ? view.height : 0;

    function place(smooth) {
      var box = element.getBoundingClientRect();
      var height = view ? view.height : window.innerHeight || 0;
      var top = view ? view.offsetTop : 0;
      var page = window.pageYOffset || document.documentElement.scrollTop || 0;
      var wanted = page + box.top + top - (height - box.height) / 2;
      var limit = Math.max(
        0,
        (document.documentElement.scrollHeight || 0) - (window.innerHeight || 0)
      );
      if (wanted < 0) wanted = 0;
      if (wanted > limit) wanted = limit;

      try {
        window.scrollTo({ top: wanted, behavior: smooth && !reduced ? 'smooth' : 'auto' });
      } catch (error) {
        window.scrollTo(0, wanted);
      }
    }

    window.requestAnimationFrame(function () {
      place(true);
    });

    window.setTimeout(function () {
      if (!view) return;
      /* Only if the keyboard actually took space. 80px is well below any
         on-screen keyboard and well above the address-bar nudge. */
      if (startHeight - view.height < 80) return;
      place(false);
    }, 340);
  }

  function syncPers(element) {
    var toggle = element.querySelector('[data-ibc-pers-toggle]');
    var entry = element.querySelector('[data-ibc-pers-entry]');
    var saved = element.querySelector('[data-ibc-pers-saved]');
    var savedText = element.querySelector('[data-ibc-pers-saved-text]');
    var input = element.querySelector('[data-ibc-pers-input]');
    var count = element.querySelector('[data-ibc-pers-count]');
    var submit = element.querySelector('[data-ibc-pers-submit]');
    var state = persState(element);

    var on = !!(toggle && toggle.checked);
    var hasSaved = on && state.saved !== '';

    if (entry) entry.hidden = !on || hasSaved;
    if (saved) saved.hidden = !hasSaved;
    if (savedText) savedText.textContent = state.saved;
    if (count && input) count.textContent = String(input.value.length);
    if (submit && input) submit.disabled = input.value.trim() === '';
    element.classList.toggle('is-on', on);
    if (!on) setPersError(element, '');
  }

  /* Visibility follows the SELECTED product, decided in Liquid and carried
   * in `data-qualifies-for`. The draft is kept when the customer switches
   * to a size that does not qualify, so switching back does not lose it. */
  function syncPersForProduct(element, productId) {
    var allowed = (element.getAttribute('data-qualifies-for') || '').split(/\s+/);
    var ok = allowed.indexOf(String(productId)) !== -1;
    element.hidden = !ok;
    if (!ok) setPersError(element, '');
  }

  /* ==================================================================== */
  /* CONTRIBUTION                                                         */
  /* ==================================================================== */

  /* Reads the visible panel and the personalised message and returns
   *   { properties: {...parent...}, items: [ ...children... ] }
   * or throws an Error whose message is shown to the customer. */
  /* Carry the offending control on the error so the caller can focus it.
   * `assets/ibc-product.js` reads `error.focusTarget` after showing the
   * message (QA D-13). */
  function withFocus(error, element) {
    if (element) error.focusTarget = element;
    return error;
  }

  function contribution(rootEl, selection, quantity) {
    var out = { properties: {}, items: [] };
    if (!rootEl || !selection || !selection.variant) return out;

    var productId = String(selection.productId);
    var handle = (selection.product && selection.product.handle) || '';
    var productTitle = (selection.product && selection.product.title) || '';
    var variant = selection.variant;
    var variantTitle = variant.title || '';
    var sku = variant.sku || '';
    var qty = quantity && quantity > 0 ? quantity : 1;

    var panel = null;
    toArray(rootEl.querySelectorAll('[data-addons-for-product]')).forEach(function (candidate) {
      if (String(candidate.getAttribute('data-addons-for-product')) === productId) panel = candidate;
    });

    var chosen = [];
    if (panel) {
      addOnsIn(panel).forEach(function (addon) {
        var toggle = addon.querySelector('input[data-ibc-addon-toggle]');
        if (!toggle || !toggle.checked) return;
        if (addon.getAttribute('data-available') !== 'true') return;

        var message = validateAddOn(addon, true);
        if (message) {
          /* QA D-13: point the customer at the control that blocked them.
           * The message is announced by `role="alert"` either way; this is
           * for keyboard and screen-magnifier users who lose their place. */
          throw withFocus(new Error(message), addon.querySelector('input[data-ibc-addon-flavour]'));
        }

        chosen.push({
          variantId: addon.getAttribute('data-variant-id'),
          title: addon.getAttribute('data-addon-title') || '',
          variantTitle: addon.getAttribute('data-variant-title') || '',
          /* `flavourText` is `flavours.join(', ')`, verbatim from the live
           * theme. Empty for an add-on with no chocolate choice. */
          flavourText: addon.getAttribute('data-has-choice') === 'true'
            ? chosenFlavours(addon).join(', ')
            : ''
        });
      });
    }

    var pers = personalisationIn(rootEl);
    var persMessage = '';
    if (pers && !pers.hidden) {
      var toggle = pers.querySelector('[data-ibc-pers-toggle]');
      if (toggle && toggle.checked) {
        var persProblem = persValidate(pers);
        if (persProblem) {
          throw withFocus(new Error(persProblem), pers.querySelector('[data-ibc-pers-input]'));
        }
        persMessage = persState(pers).saved;
      }
    }

    if (!chosen.length && !persMessage) return out;

    var key = bundleKey(handle, variant.id);

    /* `Added to cake` — the SKU segment is dropped when the parent variant
     * has no SKU, which on this store is nearly always. */
    var addedToCake = sku
      ? productTitle + ' - ' + variantTitle + ' / SKU: ' + sku
      : productTitle + ' - ' + variantTitle;

    function childBase() {
      var properties = {};
      properties[PROPS.addedToCake] = addedToCake;
      properties[PROPS.cakeProduct] = productTitle;
      properties[PROPS.cakeVariant] = variantTitle;
      properties[PROPS.cakeSku] = sku;
      properties[PROPS.cakeVariantId] = String(variant.id);
      properties[PROPS.linkedCakeKey] = key;
      return properties;
    }

    out.properties[PROPS.linkedAddOnKey] = key;

    if (chosen.length) {
      out.properties[PROPS.tripleChocolate] = 'Yes';
      out.properties[PROPS.tripleChocolateAddOn] = chosen
        .map(function (a) {
          return a.title + ' - ' + a.variantTitle;
        })
        .join(' + ');

      var choiceText = chosen
        .map(function (a) {
          return a.flavourText;
        })
        .filter(function (text) {
          return text !== '';
        })
        .join(' + ');
      if (choiceText) out.properties[PROPS.tripleChocolateChoice] = choiceText;

      chosen.forEach(function (a) {
        var properties = childBase();
        properties[PROPS.tripleChocolateSize] = a.variantTitle;
        properties[PROPS.tripleChocolateChoiceChild] = a.flavourText;
        out.items.push({ id: Number(a.variantId), quantity: qty, properties: properties });
      });
    }

    if (persMessage) {
      out.properties[PROPS.personalisedMessage] = persMessage;

      /* Deliberately NOT given `Triple chocolate size` or
       * `Triple chocolate choice`: the personalised message is not a
       * chocolate add-on and must not inherit the chocolate panel. */
      var persProperties = childBase();
      persProperties[PROPS.personalisedMessage] = persMessage;
      out.items.push({
        id: Number(pers.getAttribute('data-variant-id')),
        quantity: qty,
        properties: persProperties
      });
    }

    return out;
  }

  /* ==================================================================== */
  /* WIRING                                                               */
  /* ==================================================================== */

  function ownerOf(element) {
    return element.closest('ibc-product') || element.closest('[data-ibc-qadd-root]') || document;
  }

  function initRoot(rootEl) {
    if (rootEl._ibcAddonsBound) return;
    rootEl._ibcAddonsBound = true;

    addOnsIn(rootEl).forEach(syncAddOn);

    rootEl.addEventListener('change', function (event) {
      var target = event.target;
      if (!target || target.tagName !== 'INPUT') return;

      if (target.hasAttribute('data-ibc-addon-toggle')) {
        var addon = target.closest('[data-ibc-addon]');
        if (addon) syncAddOn(addon);
        return;
      }
      if (target.hasAttribute('data-ibc-addon-flavour')) {
        var owner = target.closest('[data-ibc-addon]');
        if (owner) onFlavourChange(owner, target);
        return;
      }
      if (target.hasAttribute('data-ibc-pers-toggle')) {
        var pers = target.closest('[data-ibc-personalisation]');
        if (!pers) return;
        if (!target.checked) persState(pers).saved = '';
        syncPers(pers);
        if (target.checked) {
          var input = pers.querySelector('[data-ibc-pers-input]');
          if (input) {
            /* Focus WITHOUT the browser's own scroll, then place the field
               ourselves. Left to itself the browser drags the nearest edge
               into view, which on a phone puts the box hard against the top
               of the screen or behind the keyboard. */
            try {
              input.focus({ preventScroll: true });
            } catch (error) {
              input.focus();
            }
            centreInView(pers.querySelector('[data-ibc-pers-entry]') || input);
          }
        }
      }
    });

    rootEl.addEventListener('input', function (event) {
      var target = event.target;
      if (!target || !target.hasAttribute || !target.hasAttribute('data-ibc-pers-input')) return;
      var pers = target.closest('[data-ibc-personalisation]');
      if (!pers) return;
      setPersError(pers, '');
      var count = pers.querySelector('[data-ibc-pers-count]');
      var submit = pers.querySelector('[data-ibc-pers-submit]');
      if (count) count.textContent = String(target.value.length);
      if (submit) submit.disabled = target.value.trim() === '';
    });

    /* Enter in the message field submits the message, never the form. */
    rootEl.addEventListener('keydown', function (event) {
      if (event.key !== 'Enter') return;
      var target = event.target;
      if (!target || !target.hasAttribute || !target.hasAttribute('data-ibc-pers-input')) return;
      event.preventDefault();
      var pers = target.closest('[data-ibc-personalisation]');
      if (pers) savePers(pers);
    });

    rootEl.addEventListener('click', function (event) {
      var button = event.target.closest('button');
      if (!button) return;
      var pers = button.closest('[data-ibc-personalisation]');
      if (!pers) return;

      if (button.hasAttribute('data-ibc-pers-submit')) {
        event.preventDefault();
        savePers(pers);
      } else if (button.hasAttribute('data-ibc-pers-edit')) {
        event.preventDefault();
        var input = pers.querySelector('[data-ibc-pers-input]');
        if (input) input.value = persState(pers).saved;
        persState(pers).saved = '';
        syncPers(pers);
        if (input) input.focus();
      } else if (button.hasAttribute('data-ibc-pers-remove')) {
        event.preventDefault();
        persState(pers).saved = '';
        var field = pers.querySelector('[data-ibc-pers-input]');
        if (field) field.value = '';
        var toggle = pers.querySelector('[data-ibc-pers-toggle]');
        if (toggle) toggle.checked = false;
        syncPers(pers);
        if (toggle) toggle.focus();
      }
    });

    var pers = personalisationIn(rootEl);
    if (pers) {
      syncPers(pers);
      /* Do NOT wait for the next `ibc:product-selection`. Both files are
       * deferred and ibc-product.js runs first, so <ibc-product>'s initial
       * synchronous dispatch happens before this listener exists and the
       * panel would stay `hidden` for the life of the page (QA D-02).
       * Read the retained selection instead, and fall back to the DOM. */
      syncPersForProduct(pers, currentProductId(rootEl));
    }
  }

  /* The product currently selected in this root, from the most reliable
   * source available: the visible add-ons panel, then the selection
   * <ibc-product> retained for late subscribers, then the root's own id. */
  function currentProductId(rootEl) {
    var panel = rootEl.querySelector('[data-addons-for-product]:not([hidden])');
    if (panel) return panel.getAttribute('data-addons-for-product');

    var owner = rootEl.closest ? rootEl.closest('ibc-product') : null;
    if (owner && typeof owner.getSelection === 'function') {
      var local = owner.getSelection();
      if (local && local.productId) return String(local.productId);
    }

    if (window.IbcProduct && typeof window.IbcProduct.getSelection === 'function') {
      var selection = window.IbcProduct.getSelection();
      if (selection && selection.productId) return String(selection.productId);
    }

    return rootEl.getAttribute('data-product-id');
  }

  function savePers(element) {
    var input = element.querySelector('[data-ibc-pers-input]');
    if (!input) return;
    var value = input.value.trim();
    if (!value) {
      setPersError(element, 'Please type a message first.');
      input.focus();
      return;
    }
    if (value.length > persMaxLength(element)) {
      setPersError(element, MSG.tooLong(persMaxLength(element)));
      input.focus();
      return;
    }
    setPersError(element, '');
    persState(element).saved = value;
    syncPers(element);
    var edit = element.querySelector('[data-ibc-pers-edit]');
    if (edit) edit.focus();
  }

  function initAll(scope) {
    toArray((scope || document).querySelectorAll('[data-ibc-addons-root]')).forEach(initRoot);
  }

  document.addEventListener('DOMContentLoaded', function () {
    initAll(document);
  });
  initAll(document);

  /* New markup arrives with the quick-add dialog and with theme-editor
   * section reloads. */
  document.addEventListener('ibc:dialog:open', function (event) {
    initAll(event.target || document);
  });
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target || document);
  });

  /* Selection drives which panel is live. assets/ibc-product.js already
   * toggles [data-addons-for-product]; this adds the personalisation gate,
   * which is per selected product and decided in Liquid. */
  document.addEventListener('ibc:product-selection', function (event) {
    var detail = event.detail || {};
    var scope = detail.element || document;

    /* An <ibc-product> living inside the quick-add dialog must not rewrite
     * the address bar of the collection page behind it. The first selection
     * is already made with `replaceUrl: false`, so stubbing here — on that
     * very first event — is early enough. */
    if (scope !== document && scope.closest && scope.closest('[data-ibc-qadd-root]')) {
      scope._syncUrl = function () {};
    }

    initAll(scope);
    toArray(scope.querySelectorAll('[data-ibc-personalisation]')).forEach(function (element) {
      syncPersForProduct(element, detail.productId);
    });
  });

  /* ---------------------------------------------------------- the seam -- */

  function register() {
    if (!window.IbcProduct || typeof window.IbcProduct.addSubmitHook !== 'function') return false;
    if (window.IbcProduct._ibcAddonsHook) return true;

    window.IbcProduct._ibcAddonsHook = window.IbcProduct.addSubmitHook(function (ctx) {
      var scope = ctx.element || document;
      var rootEl = scope.querySelector('[data-ibc-addons-root]');
      if (!rootEl) return;

      var result = contribution(rootEl, ctx.selection, ctx.quantity);
      Object.keys(result.properties).forEach(function (name) {
        ctx.properties[name] = result.properties[name];
      });
      result.items.forEach(function (item) {
        ctx.items.push(item);
      });
    });
    return true;
  }

  if (!register()) {
    /* ibc-product.js is deferred; if this file wins the race, wait for it. */
    document.addEventListener('DOMContentLoaded', register);
    window.setTimeout(register, 0);
  }

  /* ------------------------------------------------- the quick-add form -- */
  /* The dialog posts a FormData, which cannot express a second line item.
   * When the customer has actually chosen an add-on or a message we take
   * the submit over in the CAPTURE phase — before assets/ibc-quick-add.js
   * sees it — and post the same multi-item body the product page posts.
   * When nothing is chosen we do not interfere at all. */
  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!form || !form.matches || !form.matches('[data-ibc-qadd-form]')) return;

      var scope = form.closest('[data-ibc-qadd-root]') || form;
      var rootEl = scope.querySelector('[data-ibc-addons-root]');
      var owner = scope.querySelector('ibc-product') || form.closest('ibc-product');
      if (!rootEl || !owner || typeof owner.getSelection !== 'function') return;

      var selection = owner.getSelection();
      if (!selection || !selection.variant) return;

      var status = form.querySelector('[data-ibc-qadd-status]');
      var quantityField = form.querySelector('input[name="quantity"]');
      var quantity = quantityField ? parseInt(quantityField.value, 10) : 1;
      if (isNaN(quantity) || quantity < 1) quantity = 1;

      var result;
      try {
        result = contribution(rootEl, selection, quantity);
      } catch (error) {
        event.preventDefault();
        event.stopPropagation();
        if (status) status.textContent = error.message;
        return;
      }

      if (!result.items.length && !Object.keys(result.properties).length) return;

      event.preventDefault();
      event.stopPropagation();

      var parent = { id: Number(selection.variantId), quantity: quantity };
      if (Object.keys(result.properties).length) parent.properties = result.properties;

      var submit = form.querySelector('[type="submit"]');
      if (submit) submit.setAttribute('aria-disabled', 'true');
      if (status) status.textContent = 'Adding to your basket…';

      fetch(root() + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [parent].concat(result.items) })
      })
        .then(function (response) {
          return response.json().then(function (data) {
            if (!response.ok) {
              throw new Error(data.description || data.message || 'Sorry, that could not be added.');
            }
            return data;
          });
        })
        .then(function () {
          return fetch(root() + 'cart.js', { headers: { Accept: 'application/json' } })
            .then(function (response) {
              return response.json();
            })
            .catch(function () {
              return null;
            });
        })
        .then(function (cart) {
          if (status) status.textContent = 'Added to your basket.';
          document.dispatchEvent(
            new CustomEvent('ibc:cart-changed', {
              bubbles: true,
              detail: {
                cart: cart,
                item_count: cart ? cart.item_count : undefined,
                items: cart ? cart.items : undefined,
                total_price: cart ? cart.total_price : undefined,
                source: 'quick-add'
              }
            })
          );
          var dialog = document.getElementById('IbcQuickAddDialog');
          if (dialog && typeof dialog.hide === 'function') dialog.hide();
        })
        .catch(function (error) {
          if (status) status.textContent = error.message || 'Sorry, that could not be added.';
        })
        .then(function () {
          if (submit) submit.removeAttribute('aria-disabled');
        });
    },
    true
  );

  window.IbcAddOnProperties = PROPS;

  /* ------------------------------------------------- the presentation seam -- */
  /* `assets/ibc-addon-flow.js` presents these very same controls one at a
   * time, in a dialog, on the full product page. It must NOT re-implement
   * any of the rules below, so the three it needs are published here as thin
   * wrappers over the functions `contribution()` itself uses. There is still
   * exactly one implementation of each rule, and exactly one writer of the
   * line-item properties in contracts §4 — this file.
   *
   * Everything else the flow needs (which add-ons exist, their titles, their
   * prices, whether one carries a chocolate choice) it reads from the DOM
   * attributes snippets/ibc-add-ons-node.liquid already emits. It never
   * builds a property name and never posts to the cart. */
  window.IbcAddOns = {
    properties: PROPS,

    /* '' when the add-on is valid (or not selected), otherwise the exact
     * message the add-to-cart would refuse with. `announce` writes it into
     * the add-on's own inline error region as well. */
    validateAddOn: function (addon, announce) {
      if (!addon) return '';
      return validateAddOn(addon, announce !== false);
    },

    /* '' when the message is valid (or not wanted), otherwise the message. */
    validatePersonalisation: function (element) {
      if (!element) return '';
      return persValidate(element);
    },

    /* The message the customer has actually submitted, '' before they do. */
    personalisationSaved: function (element) {
      return element ? persState(element).saved : '';
    },

    /* Put a root back to "nothing chosen" after a successful add, so a
     * second add on the same page never inherits the first one's answers.
     * Every change is dispatched, so the inline error regions, the add-on
     * panels and the button's running total all resynchronise through the
     * listeners that already exist. */
    reset: function (rootEl) {
      if (!rootEl) return;
      addOnsIn(rootEl).forEach(function (addon) {
        var toggle = addon.querySelector('input[data-ibc-addon-toggle]');
        if (!toggle || !toggle.checked) return;
        toggle.checked = false;
        toggle.dispatchEvent(new Event('change', { bubbles: true }));
      });
      var element = personalisationIn(rootEl);
      if (!element) return;
      var persToggle = element.querySelector('[data-ibc-pers-toggle]');
      var input = element.querySelector('[data-ibc-pers-input]');
      persState(element).saved = '';
      if (input) input.value = '';
      if (persToggle && persToggle.checked) {
        persToggle.checked = false;
        persToggle.dispatchEvent(new Event('change', { bubbles: true }));
      } else {
        syncPers(element);
      }
    }
  };
})();
